import { FC } from "react";
import { Editor } from "@tiptap/react";
import { classNames } from "@utils/classNames";
import BoldIcon from "@public/remixicon/16px/bold.svg";
import ItalicIcon from "@public/remixicon/16px/italic.svg";
import H1Icon from "@public/remixicon/16px/h-1.svg";
import H2Icon from "@public/remixicon/16px/h-2.svg";
import H3Icon from "@public/remixicon/16px/h-3.svg";
import ParagraphIcon from "@public/remixicon/16px/parking-line.svg";
import OrderedListIcon from "@public/remixicon/16px/list-ordered.svg";
import UnorderedListIcon from "@public/remixicon/16px/list-unordered.svg";
import ClearFormattingIcon from "@public/remixicon/16px/format-clear.svg";
import { isWebUri } from "valid-url";
import { isEmail } from "@utils/validation/isEmail";

interface Props {
  editor: Editor;
}

export const BubbleMenuBar: FC<Props> = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const onLink = () => {
    const oldUrl = editor.getAttributes("link").href;
    const oldUrlCleansed = oldUrl?.replace("mailto:", "");
    console.log({ oldUrl, oldUrlCleansed });

    let url: string | null;
    // !(url === "" || url === null || isValidWebUrl || isValidEmailUrl)
    while (true) {
      url = window.prompt("Enter link or email", url || oldUrlCleansed);

      // is email?
      const isValidEmailUrl = isEmail(url);
      if (isValidEmailUrl) {
        url = `mailto:${url}`;
        console.log("EMAIL", { url, isValidEmailUrl });
        break;
      }

      // is web link?
      const isValidWebUrl = !!isWebUri(url);
      if (isValidWebUrl) {
        console.log("WEB LINK", { url, isValidWebUrl });
        break;
      }

      // const urlWithHardcodedSchema = `https://${url}`;
      // const isValidWebUrlWithoutSchema = !!isWebUri(urlWithHardcodedSchema);
      // if (isValidWebUrlWithoutSchema) {
      //   url = urlWithHardcodedSchema;
      //   console.log("WEB LINK WITHOUT SCHEMA", {
      //     url,
      //     isValidWebUrlWithoutSchema,
      //   });
      //   break;
      // }

      // is empty string or null?
      if (url === "" || url === null) {
        console.log("OTHER", { url, isValidEmailUrl, isValidWebUrl });
        break;
      }

      console.log("INVALID", { url, isValidWebUrl });
      alert("Invalid url. Does it start with https:// ?");
    }

    // handle cancel -> value will be null
    if (url === null) {
      return;
    }

    // handle removing link -> value will be empty string
    if (url === "") {
      return editor.chain().focus().unsetLink().run();
    }

    // handle setting link -> value will be non-empty string
    if (!!url) {
      return editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex flex-row divide-x divide-gray-200 bg-white border border-gray-300 rounded-md shadow-md shadow-black/40">
      <div>
        <button
          title="bold"
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={classNames(
            editor.isActive("bold") ? "bg-blue-100" : "hover:bg-gray-100",
            "px-1.5 py-1.5 m-px rounded"
          )}
        >
          <BoldIcon
            className={classNames(
              editor.isActive("bold") ? "text-blue-600" : "text-gray-600",
              "fill-current"
            )}
          />
        </button>
        <button
          title="italic"
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={classNames(
            editor.isActive("italic") ? "bg-blue-100" : "hover:bg-gray-100",
            "px-1.5 py-1.5 m-px rounded"
          )}
        >
          <ItalicIcon
            className={classNames(
              editor.isActive("italic") ? "text-blue-600" : "text-gray-600",
              "fill-current"
            )}
          />
        </button>
      </div>

      <div>
        <button
          title="heading 1"
          type="button"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          className={classNames(
            editor.isActive("heading", { level: 1 })
              ? "bg-blue-100"
              : "hover:bg-gray-100",
            "px-1.5 py-1.5 m-px rounded"
          )}
        >
          <H1Icon
            className={classNames(
              editor.isActive("heading", { level: 1 })
                ? "text-blue-600"
                : "text-gray-600",
              "fill-current"
            )}
          />
        </button>
        <button
          title="heading 2"
          type="button"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          className={classNames(
            editor.isActive("heading", { level: 2 })
              ? "bg-blue-100"
              : "hover:bg-gray-100",
            "px-1.5 py-1.5 m-px rounded"
          )}
        >
          <H2Icon
            className={classNames(
              editor.isActive("heading", { level: 2 })
                ? "text-blue-600"
                : "text-gray-600",
              "fill-current"
            )}
          />
        </button>
        <button
          title="heading 3"
          type="button"
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          className={classNames(
            editor.isActive("heading", { level: 3 })
              ? "bg-blue-100"
              : "hover:bg-gray-100",
            "px-1.5 py-1.5 m-px rounded"
          )}
        >
          <H3Icon
            className={classNames(
              editor.isActive("heading", { level: 3 })
                ? "text-blue-600"
                : "text-gray-600",
              "fill-current"
            )}
          />
        </button>
        <button
          title="paragraph"
          type="button"
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={classNames(
            editor.isActive("paragraph") ? "bg-blue-100" : "hover:bg-gray-100",
            "px-1.5 py-1.5 m-px rounded"
          )}
        >
          <ParagraphIcon
            className={classNames(
              editor.isActive("paragraph") ? "text-blue-600" : "text-gray-600",
              "fill-current"
            )}
          />
        </button>
      </div>

      <div>
        <button
          title="bullet list"
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={classNames(
            editor.isActive("bulletList") ? "bg-blue-100" : "hover:bg-gray-100",
            "px-1.5 py-1.5 m-px rounded"
          )}
        >
          <UnorderedListIcon
            className={classNames(
              editor.isActive("bulletList") ? "text-blue-600" : "text-gray-600",
              "fill-current"
            )}
          />
        </button>
        <button
          title="ordered list"
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={classNames(
            editor.isActive("orderedList")
              ? "bg-blue-100"
              : "hover:bg-gray-100",
            "px-1.5 py-1.5 m-px rounded"
          )}
        >
          <OrderedListIcon
            className={classNames(
              editor.isActive("orderedList")
                ? "text-blue-600"
                : "text-gray-600",
              "fill-current"
            )}
          />
        </button>
      </div>

      <div>
        <button
          title="clear formatting"
          type="button"
          onClick={() =>
            editor.chain().focus().clearNodes().unsetAllMarks().run()
          }
          className={classNames("hover:bg-gray-100", "px-1.5 py-1.5 m-px rounded")}
        >
          <ClearFormattingIcon className="text-gray-600 fill-current" />
        </button>
      </div>
    </div>
  );
};
